import debounce from "lodash.debounce";

import { device } from "@utilities";

const CLASS_PREFIX = "js-horizontal-panel";
const DISPLAY_NONE_CLASS = "hidden";
const ACTIVE_CLASS = "is-active";
const PANEL_IS_DRAGGING_CLASS = "is-dragging";

const SCROLL_ARROW_BUTTON_WITH_SHADOW_WIDTH = 64;

/**
 * Scroll on active panel button
 */
export const handleActivePanelButtonVisibility = ({
  containerEl,
  itemElement,
  callback,
}: {
  containerEl: HTMLElement;
  itemElement: Element | HTMLElement;
  callback?: () => void | null;
}): void => {
  const { offsetLeft: buttonOffsetLeft } = itemElement as HTMLElement;

  // eslint-disable-next-line no-param-reassign
  containerEl.scrollLeft = buttonOffsetLeft - SCROLL_ARROW_BUTTON_WITH_SHADOW_WIDTH;

  if (callback) {
    callback();
  }
};

/**
 * Handle horizontal scroll panel behaviour with buttons
 *
 * This horizontal panel scroll is used in Tabs and Comparator widgets (so far).
 *
 * For next usage, look at those two examles in .cshtml templates to see, how
 * it is structured
 *
 * @param container {HTMLElement} container element
 */
const HorizontalScrollPanel = (container: HTMLElement): void => {
  // Helper variables for panel scroll
  let activeButtonEl: HTMLElement = null;
  let wasPanelScrolled = false;
  let isPanelMouseDown = false;
  let listScrollStartX = 0;
  let listScrollLeft = 0;

  const buttonListEl: HTMLUListElement = container.querySelector(`.${CLASS_PREFIX}-button-list`);
  const { parentElement: buttonListParent } = buttonListEl;
  const scrollPrevButton: HTMLButtonElement = container.querySelector(
    `.${CLASS_PREFIX}-prev-button`
  );
  const scrollNextButton: HTMLButtonElement = container.querySelector(
    `.${CLASS_PREFIX}-next-button`
  );

  // Node list of panel buttons
  const panelButtonArr: NodeListOf<HTMLAnchorElement> = buttonListEl.querySelectorAll(
    `.${CLASS_PREFIX}-button`
  );

  const hideScrollButtons = (): void => {
    scrollPrevButton.classList.add(DISPLAY_NONE_CLASS);
    scrollNextButton.classList.add(DISPLAY_NONE_CLASS);

    wasPanelScrolled = true;
  };

  /**
   * Handle panel mouse down event
   */
  const handlePanelMouseDown = (event: MouseEvent): void => {
    const { offsetLeft: parentOffsetLeft } = buttonListParent;

    isPanelMouseDown = true;

    // Starting panel scroll left position
    listScrollLeft = buttonListEl.scrollLeft;
    // Parent container x position
    listScrollStartX = event.pageX - parentOffsetLeft;
  };

  /**
   * Handle panel mouse up event
   */
  const handlePanelMouseUp = (): void => {
    isPanelMouseDown = false;

    buttonListEl.classList.remove(PANEL_IS_DRAGGING_CLASS);
  };

  /**
   * Handle panel mouse move event
   */
  const handlePanelMouseMove = (event: MouseEvent): void => {
    event.preventDefault();

    if (!isPanelMouseDown) return;

    // Add dragging class to panel to prevent text selection and prevent panel button clicks
    buttonListEl.classList.add(PANEL_IS_DRAGGING_CLASS);
    // Hide scroll buttons
    hideScrollButtons();

    const xPosition = event.pageX - buttonListParent.offsetLeft;
    const scrollPosition = xPosition - listScrollStartX;

    buttonListEl.scrollLeft = listScrollLeft - scrollPosition;
  };

  /**
   * Set scroll buttons visibility based on scroll list X position
   */
  const setScrollButtonsDefaultVisibility = ({
    isLeftArrowVisible = true,
    isRightArrowVisible = true,
  }): void => {
    if (isLeftArrowVisible) {
      scrollPrevButton.classList.remove(DISPLAY_NONE_CLASS);
    }

    if (isRightArrowVisible) {
      scrollNextButton.classList.remove(DISPLAY_NONE_CLASS);
    }
  };

  /**
   * Check if panel with buttons is scrollable
   * and if so show scroll buttons.
   *
   * Show scroll buttons only when user did not scroll the panel yet (by drag or by scroll button click)
   */
  const detectScrollablePanel = (): void => {
    const { clientWidth: parentWidth } = buttonListParent;
    const BUTTON_ITEM_RIGHT_MARGIN = 4;

    /**
     * It is not possible to count with parent element width due to padding,
     * so sum up button elements
     */
    const panelChildrenWidth: number = Array.from(panelButtonArr).reduce((acc, curr) => {
      const { width } = curr.getBoundingClientRect();

      const newWidth = acc + width + BUTTON_ITEM_RIGHT_MARGIN;

      return newWidth;
    }, -BUTTON_ITEM_RIGHT_MARGIN);

    const isScrollable = panelChildrenWidth > parentWidth;

    if (!isScrollable) {
      hideScrollButtons();

      buttonListEl.removeEventListener("mousedown", handlePanelMouseDown);
      buttonListEl.removeEventListener("mouseup", handlePanelMouseUp);
      buttonListEl.removeEventListener("mouseleave", handlePanelMouseUp);
      buttonListEl.removeEventListener("mousemove", handlePanelMouseMove);

      buttonListEl.removeEventListener("touchmove", hideScrollButtons);

      return;
    }

    buttonListEl.addEventListener("mousedown", handlePanelMouseDown);
    buttonListEl.addEventListener("mouseup", handlePanelMouseUp);
    buttonListEl.addEventListener("mouseleave", handlePanelMouseUp);
    buttonListEl.addEventListener("mousemove", handlePanelMouseMove);

    // Mobile move
    buttonListEl.addEventListener("touchmove", hideScrollButtons);

    if (wasPanelScrolled) return;

    // Set visibility on scroll arrow buttons
    const {
      scrollLeft: panelListScrollLeft,
      scrollWidth: panelListScrollWidth,
      offsetWidth: panelListOffsetWidth,
    } = buttonListEl;

    let isLeftArrowVisible = false;
    let isRightArrowVisible = false;

    if (panelListScrollLeft > 0) {
      isLeftArrowVisible = true;
    }

    if (panelListOffsetWidth + panelListScrollLeft < panelListScrollWidth) {
      isRightArrowVisible = true;
    }

    setScrollButtonsDefaultVisibility({ isLeftArrowVisible, isRightArrowVisible });
  };

  /**
   * Handle scroll button click
   */
  const handleScrollButtonClick = ({ side }: { side: "left" | "right" }): void => {
    const { clientWidth: panelParentWidth } = buttonListParent;
    const { scrollLeft: actualScrollLeftPosition } = buttonListEl;

    const MOBILE_SCROLL_PERCENTAGE_DISTANCE = 80;
    const DESKTOP_SCROLL_PERCENTAGE_DISTANCE = 40;

    const isMobile = device.isMobileBreakpoint();

    const scrollDistance =
      (panelParentWidth / 100) *
      (isMobile ? MOBILE_SCROLL_PERCENTAGE_DISTANCE : DESKTOP_SCROLL_PERCENTAGE_DISTANCE);

    const scrollLeft =
      side === "right"
        ? actualScrollLeftPosition + scrollDistance
        : actualScrollLeftPosition - scrollDistance;

    buttonListEl.scrollLeft = scrollLeft;

    // Hide button after click scroll
    hideScrollButtons();
  };

  /**
   * Handle mobile visibility of panel button
   */
  const handleMobilePanelButtonVisibility = (entry: IntersectionObserverEntry): void => {
    const { intersectionRatio, boundingClientRect } = entry;

    const isButtonOverflowed = intersectionRatio !== 1;

    if (!isButtonOverflowed) return;

    const { x: xPosition, right } = boundingClientRect;

    const isOverflowedToLeft = xPosition < 0;
    const isOverflowedToRight = right > device.getDeviceWidth();

    // If button is overflowing on both sides, try to position it its left side
    if (isOverflowedToLeft && isOverflowedToRight) {
      handleActivePanelButtonVisibility({
        containerEl: buttonListEl,
        itemElement: entry.target,
      });
      return;
    }

    const panelListScrollLeft = buttonListEl.scrollLeft;
    const ADDITIONAL_PANEL_GAP = 16;

    if (isOverflowedToLeft) {
      const isLeftArrowVisible = !scrollPrevButton.classList.contains(DISPLAY_NONE_CLASS);

      const narrowSize =
        ADDITIONAL_PANEL_GAP + (isLeftArrowVisible ? SCROLL_ARROW_BUTTON_WITH_SHADOW_WIDTH : 0);

      // xPosition is negative == scroll to left
      buttonListEl.scrollLeft = panelListScrollLeft + xPosition - narrowSize;
      return;
    }

    if (isOverflowedToRight) {
      const isRightArrowVisible = !scrollNextButton.classList.contains(DISPLAY_NONE_CLASS);
      const overflowedPart = right - device.getDeviceWidth();

      const narrowSize =
        ADDITIONAL_PANEL_GAP + (isRightArrowVisible ? SCROLL_ARROW_BUTTON_WITH_SHADOW_WIDTH : 0);

      // xPosition is negative == scroll to left
      buttonListEl.scrollLeft = panelListScrollLeft + overflowedPart + narrowSize;
    }
  };

  /**
   * Handle desktop visibility of panel button
   */
  const handleDesktopPanelButtonVisibility = (entry: IntersectionObserverEntry): void => {
    const { intersectionRect, boundingClientRect } = entry;

    const isButtonFullyVisible = intersectionRect.width === boundingClientRect.width;

    if (isButtonFullyVisible) return;

    const isOverflowedToLeft = boundingClientRect.x < intersectionRect.x;
    const isOverflowedToRight = intersectionRect.right < boundingClientRect.right;

    // If button is overflowing on both sides, try to position it its left side
    if (isOverflowedToLeft && isOverflowedToRight) {
      handleActivePanelButtonVisibility({
        containerEl: buttonListEl,
        itemElement: entry.target,
      });

      handleActivePanelButtonVisibility({
        containerEl: buttonListEl,
        itemElement: entry.target,
      });
      return;
    }

    const panelListScrollLeft = buttonListEl.scrollLeft;
    const ADDITIONAL_PANEL_GAP = 16;

    if (isOverflowedToLeft) {
      const isLeftArrowVisible = !scrollPrevButton.classList.contains(DISPLAY_NONE_CLASS);

      const overflowedPart = intersectionRect.x - boundingClientRect.x;
      const narrowSize =
        ADDITIONAL_PANEL_GAP + (isLeftArrowVisible ? SCROLL_ARROW_BUTTON_WITH_SHADOW_WIDTH : 0);

      // overflowedPart is negative == scroll to left
      buttonListEl.scrollLeft = panelListScrollLeft - overflowedPart - narrowSize;

      return;
    }

    if (isOverflowedToRight) {
      const isRightArrowVisible = !scrollNextButton.classList.contains(DISPLAY_NONE_CLASS);

      const overflowedPart = boundingClientRect.right - intersectionRect.right;

      const narrowSize =
        ADDITIONAL_PANEL_GAP + (isRightArrowVisible ? SCROLL_ARROW_BUTTON_WITH_SHADOW_WIDTH : 0);

      // xPosition is negative == scroll to left
      buttonListEl.scrollLeft = panelListScrollLeft + overflowedPart + narrowSize;
    }
  };

  /**
   * Check if clicked panel button is fully visible on click and
   * move it to screen view if it's not
   */
  const checkClickedPanelButtonVisibility = (buttonEl: HTMLElement): void => {
    const observer = new IntersectionObserver(([entry]) => {
      const isMobile = device.isMobileBreakpoint();

      if (isMobile) {
        handleMobilePanelButtonVisibility(entry);
      } else {
        handleDesktopPanelButtonVisibility(entry);
      }

      observer.disconnect();
    });

    observer.observe(buttonEl);
  };

  /**
   * Handle panel button click
   */
  const handleButtonClick = (event: MouseEvent): void => {
    // Prevent scroll jump on item
    event.preventDefault();

    const buttonEl: HTMLElement = event.currentTarget as HTMLElement;

    /* Ignore repeated clicks on active button */
    if (buttonEl === activeButtonEl) return;

    /* Set helper variable for condition check 2 lines up */
    activeButtonEl = buttonEl;

    checkClickedPanelButtonVisibility(buttonEl);
  };

  const prepareDefaultState = (): void => {
    activeButtonEl = Array.from(panelButtonArr).find((item) =>
      item.classList.contains(ACTIVE_CLASS)
    );

    // Apply click listener on every panel button
    panelButtonArr.forEach((button) => {
      button.addEventListener("click", handleButtonClick);
    });

    // Apply click listener on scrolling panel buttons + handle button visibility
    scrollPrevButton.addEventListener("click", () => handleScrollButtonClick({ side: "left" }));
    scrollNextButton.addEventListener("click", () => handleScrollButtonClick({ side: "right" }));

    detectScrollablePanel();
  };

  // Proceed height animation on resize event
  window.addEventListener(
    "resize",
    debounce(() => {
      detectScrollablePanel();

      const activePanelButton = Array.from(panelButtonArr).find((button) =>
        button.classList.contains(ACTIVE_CLASS)
      );
      checkClickedPanelButtonVisibility(activePanelButton);
    }, 200)
  );

  // Prepare default state after 100ms timeout
  window.setTimeout(() => {
    prepareDefaultState();
  }, 100);
};

export default HorizontalScrollPanel;
